/**
 * The positive skill chips of the WHOLE conversation thread: the live bubbles'
 * (`selectSkillChips`, decided from the live events) plus the history's — the
 * earlier part of the conversation seeded by "Continuar" or a reload, whose
 * bubbles carry no live events. The history's chips are rebuilt from its
 * session summary exactly as the history's session detail does
 * (`selectTranscriptSkillChips`, by turn index), and land on the historical
 * bubble that is that turn (`VoiceMessage.historicalTurn`).
 *
 * The history is ONE session (the transcript of the session being resumed or
 * reattached). Two cases:
 *   - "Continuar": the history is the PARENT session, the live one is new.
 *     Once-per-session rules are per session, so both sides keep their chips.
 *   - A reload's reattach: the history is the SAME session as the live part.
 *     The live selector already counts the earlier turns it cannot see as
 *     shown (a chip may be withheld, never repeated); on top of that, a live
 *     chip is dropped when a historical bubble of the session already shows
 *     the same skill or "nailed" the same radar focus — except a completion,
 *     which is news after an earlier rep of its skill (as live), unless the
 *     history already shows that completion.
 *
 * The sparkles (`completionCount`) and the debrief gate never read this map:
 * they stay on the live chips, so a reload never celebrates again.
 */
import type { VoiceMessage } from '@orborix/voice-client';
import { selectTranscriptSkillChips } from '../activity/transcriptSkillChips.js';
import type { UiLocale } from './coachCatalog.js';
import type { HistorySkillCredits } from './hooks/useSessionLifecycle.js';
import type { SkillChipModel } from './skillChips.js';

export function selectThreadSkillChips(
  messages: ReadonlyArray<VoiceMessage>,
  liveChips: ReadonlyMap<number, SkillChipModel>,
  history: HistorySkillCredits | null,
  liveSessionId: string | null | undefined,
  locale: UiLocale = 'es',
): ReadonlyMap<number, SkillChipModel> {
  if (!history || history.credits.length === 0) return liveChips;
  const byTurn = selectTranscriptSkillChips(history.credits, locale);
  if (byTurn.size === 0) return liveChips;

  const out = new Map<number, SkillChipModel>();
  /** Skills the history shows a chip of, with whether one is the completion. */
  const shownSkills = new Map<string, { completed: boolean }>();
  /** Radar foci the history's chips praised. */
  const shownFoci = new Set<string>();
  const placedTurns = new Set<number>();
  messages.forEach((m, i) => {
    const turn = m.historicalTurn;
    if (m.who !== 'user' || !m.historical || turn?.sessionId !== history.sessionId) return;
    const chip = byTurn.get(turn.turnIndex);
    if (!chip || placedTurns.has(turn.turnIndex)) return;
    placedTurns.add(turn.turnIndex);
    out.set(i, chip);
    const prev = shownSkills.get(chip.skillId);
    shownSkills.set(chip.skillId, {
      completed: (prev?.completed ?? false) || chip.kind === 'completed',
    });
    if (chip.kind !== 'grammar') {
      const credit = history.credits.find(
        (c) => c.turnIndex === turn.turnIndex && c.skillId === chip.skillId,
      );
      if (credit?.nailedFocus) shownFoci.add(credit.nailedFocus.focusId);
    }
  });

  const sameSession = liveSessionId != null && liveSessionId === history.sessionId;
  for (const [i, chip] of liveChips) {
    if (sameSession && repeatsHistory(messages[i], chip, shownSkills, shownFoci)) continue;
    out.set(i, chip);
  }
  return out;
}

function repeatsHistory(
  m: VoiceMessage | undefined,
  chip: SkillChipModel,
  shownSkills: ReadonlyMap<string, { completed: boolean }>,
  shownFoci: ReadonlySet<string>,
): boolean {
  const shown = shownSkills.get(chip.skillId);
  if (chip.kind === 'completed') return shown?.completed === true;
  if (shown) return true;
  if (chip.kind !== 'nailed') return false;
  const focusId = m?.skillCredits?.find((c) => c.skillId === chip.skillId)?.nailedFocus?.focusId;
  return focusId !== undefined && shownFoci.has(focusId);
}
